import { Component, OnDestroy, inject, output, computed } from '@angular/core';
import { MatIcon, MatIconModule } from '@angular/material/icon';
import {
  NON_MESSAGE_TASKS,
  TaskInstanceDetail,
  TaskStatus,
} from '@data-access/tasks';
import { ERMDateTimePipe } from '@shared/pipes/erm-date.pipe';
import { TaskHeaderComponent } from '../task-header/task-header.component';
import { NgTemplateOutlet } from '@angular/common';
import { FullNamePipe } from '@shared/pipes/fullname.pipe';
import {
  TASK_DETAIL_FACADE_SERVICE,
  TASK_FACADE_SERVICE,
} from '@data-access/tasks/store/token';
import {
  TasksDetailFacadeService,
  TasksFacadeService,
} from '@data-access/tasks/facade';
import { HandleSideBarService } from '@data-access/shared';
import { ActivatedRoute, Router } from '@angular/router';
import {
  WcgBannerComponent,
  WcgButtonComponent,
} from '@wcg/frontend-framework';
import {
  SubmissionDetailFacadeService,
  SUBMISSSION_DETAILS_V2_PATH,
} from '@data-access/submissions';
import { TaskMessagingComponent } from './task-messaging';
import { AssigneeComponent } from '@submissions/next/ui-components/assignee';
import { GoBackToListComponent } from '@submissions/next/ui-components/gobacktolist';
import { SIDEBAR_COMPONENT_LOADER } from '@data-access/shared/constants/sidebar-component-loader';
@Component({
  imports: [
    MatIconModule,
    ERMDateTimePipe,
    TaskHeaderComponent,
    NgTemplateOutlet,
    WcgButtonComponent,
    MatIconModule,
    MatIcon,
    WcgBannerComponent,
    TaskMessagingComponent,
    AssigneeComponent,
    GoBackToListComponent,
  ],
  providers: [
    ERMDateTimePipe,
    FullNamePipe,
    {
      provide: TASK_FACADE_SERVICE,
      useExisting: TasksFacadeService,
    },
    {
      provide: TASK_DETAIL_FACADE_SERVICE,
      useExisting: TasksDetailFacadeService,
    },
    HandleSideBarService,
    SubmissionDetailFacadeService,
  ],
  selector: 'wcg-task-detail',
  styleUrl: './task-detail.component.scss',
  templateUrl: './task-detail.component.html',
})
export class TaskDetailComponent implements OnDestroy {
  private readonly _tasksFacadeService = inject(TasksFacadeService);
  private readonly _tasksDetailFacadeService = inject(TasksDetailFacadeService);
  private readonly _handleSiderBarService = inject(HandleSideBarService);
  private readonly _activatedRoute = inject(ActivatedRoute);
  private readonly _router = inject(Router);
  private readonly _submissionDetailFacadeService = inject(
    SubmissionDetailFacadeService,
  );

  taskDetails = this._tasksDetailFacadeService.$taskDetail;
  isLoadingTaskDetail = this._tasksDetailFacadeService.$isLoadingTaskDetail;
  $currentStage = this._submissionDetailFacadeService.$currentStage;
  $submission = this._submissionDetailFacadeService.$submission;
  $submissionId = this._submissionDetailFacadeService.$submissionId;
  $filtered = this._tasksFacadeService.$filtered;
  $filteredTask = this._tasksDetailFacadeService.$filteredTask;
  /*
   * Computed Signals Start
   */
  $isMessagingEnabled = computed(() => {
    const stage = this.$currentStage();
    const task = this.taskDetails();
    if (!task) return false;
    return stage === 'qc_check' && !NON_MESSAGE_TASKS.has(task.task_type);
  });
  /*
   * Computed Signals End
   */

  // Computed signal that groups documents by timestamp and author
  $groupedDocuments = this._tasksDetailFacadeService.$groupedDocuments;

  /*
   * Output Signals Start
   */
  allTaskList = output<void>();
  viewQuestionSection = output<TaskInstanceDetail>();
  /*
   * Output Signals End
   */

  goBackToAllTaskList() {
    if (this.$filtered() && !this.$filteredTask()) {
      this._tasksFacadeService.loadTaskList(
        this.$submissionId()!,
        this.$submission()?.current_stage || '',
      );
    }

    SIDEBAR_COMPONENT_LOADER['task-list']().then((component) => {
      this._handleSiderBarService.setSideBarComponent(component);
    });
  }

  /*
   * Lifecycle Hooks
   */
  ngOnDestroy(): void {
    this._tasksDetailFacadeService.clearTaskDetail();
  }
  /*
   * Lifecycle Hooks End
   */

  viewQuestion() {
    this.viewQuestionSection.emit(this.taskDetails()!);
  }

  viewDocument(docId: string) {
    const _submissionId = this._activatedRoute.snapshot.params[
      'submissionId'
    ] as string;
    this._router.navigateByUrl(
      `/${SUBMISSSION_DETAILS_V2_PATH}/${_submissionId}/documents/${docId}`,
    );
  }

  uploadTaskDocument() {
    this._router.navigate([`./upload-documents`], {
      relativeTo: this._activatedRoute,
    });
  }

  changeStatus(newStatus: string) {
    this._tasksDetailFacadeService.updateTaskStatus(
      this.taskDetails()!.workspace_id,
      this.taskDetails()!.id,
      newStatus as TaskStatus,
    );
  }
}
